/**
 * A chord box, drawn the way chord charts draw it: six vertical strings, a window
 * of five frets, dots where fingers go, o for an open string, x for a muted one,
 * and a bar across the fret when the shape needs a barre.
 *
 * Drawn by hand rather than via VexFlow because we want the muted/open row, the
 * position marker, and the brass colour language of the rest of the app.
 */

import type { GuitarVoicing } from '../theory/guitar';

const STRINGS = 6;
const WINDOW = 5;

export interface FretDiagramProps {
  voicing: GuitarVoicing;
  width?: number;
}

export function FretDiagram({ voicing, width = 168 }: FretDiagramProps) {
  const padX = width * 0.16;
  const padTop = width * 0.2;
  const boardWidth = width - padX * 2;
  const stringGap = boardWidth / (STRINGS - 1);
  const fretGap = stringGap * 1.18;
  const boardHeight = fretGap * WINDOW;
  const height = padTop + boardHeight + width * 0.1;

  const fretted = voicing.frets.filter((f): f is number => f !== null && f > 0);
  const hasOpenString = voicing.frets.some((f) => f === 0);
  const highestFret = fretted.length ? Math.max(...fretted) : 0;

  // Where the five-fret window starts. An open string only means anything relative
  // to the nut, so any shape using one must be drawn from the nut. Shapes that fit
  // inside the first five frets are drawn there too, because that is how they are
  // learned; only shapes further up the neck get a position number.
  const startFret =
    hasOpenString || !fretted.length || highestFret <= WINDOW ? 1 : Math.min(...fretted);
  const atNut = startFret === 1;

  const stringX = (index: number) => padX + index * stringGap;
  const fretY = (fret: number) => padTop + (fret - startFret + 1) * fretGap;
  const dotY = (fret: number) => fretY(fret) - fretGap / 2;

  return (
    <svg
      className="fret-diagram"
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      role="img"
      aria-label={`Chord diagram, frets ${voicing.frets
        .map((f) => (f === null ? 'muted' : f === 0 ? 'open' : f))
        .join(', ')} from the low string`}
    >
      {/* nut or position marker */}
      {atNut ? (
        <rect x={padX - 1} y={padTop - 4} width={boardWidth + 2} height={5} className="fd-nut" />
      ) : (
        <text x={padX - stringGap * 0.6} y={dotY(startFret) + 4} className="fd-position">
          {startFret}
        </text>
      )}

      {/* frets */}
      {Array.from({ length: WINDOW + 1 }, (_, i) => (
        <line
          key={`f${i}`}
          className="fd-fret"
          x1={padX}
          x2={padX + boardWidth}
          y1={padTop + i * fretGap}
          y2={padTop + i * fretGap}
        />
      ))}

      {/* strings */}
      {Array.from({ length: STRINGS }, (_, i) => (
        <line
          key={`s${i}`}
          className="fd-string"
          x1={stringX(i)}
          x2={stringX(i)}
          y1={padTop}
          y2={padTop + boardHeight}
        />
      ))}

      {/* barre */}
      {voicing.barre !== null
        ? (() => {
            const barred = voicing.frets
              .map((f, i) => (f === voicing.barre ? i : -1))
              .filter((i) => i >= 0);
            const from = Math.min(...barred);
            const to = Math.max(...barred);
            return (
              <rect
                className="fd-barre"
                x={stringX(from) - stringGap * 0.28}
                y={dotY(voicing.barre) - stringGap * 0.28}
                width={stringX(to) - stringX(from) + stringGap * 0.56}
                height={stringGap * 0.56}
                rx={stringGap * 0.28}
              />
            );
          })()
        : null}

      {/* open / muted markers and finger dots */}
      {voicing.frets.map((fret, i) => {
        if (fret === null) {
          return (
            <text key={`m${i}`} className="fd-mute" x={stringX(i)} y={padTop - 8}>
              ×
            </text>
          );
        }
        if (fret === 0) {
          return (
            <circle
              key={`o${i}`}
              className="fd-open"
              cx={stringX(i)}
              cy={padTop - 12}
              r={stringGap * 0.22}
            />
          );
        }
        if (fret === voicing.barre) return null; // covered by the bar
        return (
          <circle
            key={`d${i}`}
            className="fd-dot"
            cx={stringX(i)}
            cy={dotY(fret)}
            r={stringGap * 0.3}
          />
        );
      })}
    </svg>
  );
}
